import React, { useEffect, useRef, useState } from 'react';
import SharedCardContent from '@shared/SharedCardContent/SharedCardContent';
import Button from '@shared/Button/Button';
import './InstantCiteVerification.css';
import { motion, useInView } from 'framer-motion';
import quoteIcon from '@icons/quote-icon.svg';
import fileIcon from '@icons/instant-file-upload.svg';
import fileDrag from '@icons/instant-file-drag.svg';
import mouseArrow from '@icons/instant-mouse-arrow.svg';

function InstantCiteVerification({ visualOnly = false }) {
    const sectionRef = useRef(null);
    const inView = useInView(sectionRef, { once: false, margin: '-140px' });

    const [step, setStep] = useState(0); // 0=idle, 1=dragging, 2=uploading, 3=cta, 4=result
    const [clicked, setClicked] = useState(false);
    const [topClicked, setTopClicked] = useState(false); // first-step CTA click

    useEffect(() => {
        if (!inView) { setStep(0); return; }
        setStep(1);
    }, [inView]);

    useEffect(() => {
        let t1, t2, t3, t4, tc1, tc2, tTop1, tTop2;
        if (step === 1) {
            // Reset first CTA state; play drag, then tap button and go next
            setTopClicked(false);
            // trigger button tap slightly before moving to uploading
            tTop1 = setTimeout(() => setTopClicked(true), visualOnly ? 900 : 3600);
            t1 = setTimeout(() => setStep(2), visualOnly ? 1200 : 4200);
        } else if (step === 2) {
            t2 = setTimeout(() => setStep(3), visualOnly ? 900 : 2600);
        } else if (step === 3) {
            setClicked(false);
            tc1 = setTimeout(() => setClicked(true), visualOnly ? 350 : 700);
            tc2 = setTimeout(() => setStep(4), visualOnly ? 700 : 1400);
        }
        // Step 4 (the verified result) holds; it replays only when the card comes back into view.
        // It used to jump straight back to the upload screen after a few seconds, forever.
        return () => { clearTimeout(t1); clearTimeout(t2); clearTimeout(t3); clearTimeout(t4); clearTimeout(tc1); clearTimeout(tc2); clearTimeout(tTop1); clearTimeout(tTop2); };
    }, [step, visualOnly]);

    // Origins and targets
    const origin = { x: 200, y: 186 }; // bottom-right of dropzone
    const target = { x: 0, y: 0 };    // center of dropzone

    // Bring both cursor and file toward the "Click to upload" link (left + slightly up)
    const targetOffset = { x: 120, y: 50 }; // tweak here to align with the link

    // Cursor relative offset to file (keeps cursor under file)
    const cursorOffset = { x: -8, y: 32 };

    return (
        <div className={`instant-cite-container${visualOnly ? " instant-cite-container--visual-only" : ""}`} ref={sectionRef}>
            <div className="instant-cite-left">
                <div className="instant-cite-left-content">
                    <div className="instant-cite-upload-card">
                        {(step === 0 || step === 1) && (
                            <div className="instant-cite-dropzone dz-relative">
                                <img src={fileIcon} alt="upload" className="instant-cite-upload-icon" />
                                <div className="instant-cite-dropzone-text">
                                    <span className="link">Click to upload</span> or drag and drop
                                </div>
                                <div className="instant-cite-hint">PDF, DOCX, DOC or TXT (max. 10MB)</div>

                                <motion.img
                                    key="drag-file"
                                    src={fileDrag}
                                    alt="file-drag"
                                    className="icv-drag-file"
                                    initial={{ x: origin.x, y: origin.y, rotate: 12, opacity: 1 }}
                                    animate={{ x: step === 1 ? target.x + targetOffset.x + cursorOffset.x : origin.x, y: step === 1 ? target.y + targetOffset.y + cursorOffset.y : origin.y, rotate: step === 1 ? 0 : 12, opacity: 1 }}
                                    transition={{ duration: 2.4, ease: 'easeOut' }}
                                />
                                <motion.img
                                    key="drag-cursor"
                                    src={mouseArrow}
                                    alt="cursor"
                                    className="icv-drag-cursor"
                                    initial={{ x: origin.x + cursorOffset.x, y: origin.y + cursorOffset.y, opacity: 1 }}
                                    animate={{ x: step === 1 ? target.x + targetOffset.x + cursorOffset.x : origin.x + cursorOffset.x, y: step === 1 ? target.y + targetOffset.y + cursorOffset.y : origin.y + cursorOffset.y, opacity: 0 }}
                                    transition={{ duration: 2.4, ease: 'easeOut' }}
                                />
                            </div>
                        )}

                        {step === 2 && (
                            <div className="instant-cite-uploading">

                                <div className="file-row">
                                    <img src={fileIcon} alt="file" />
                                    <div >
                                        <div className="file-meta">
                                            <div className="file-name">Filing 1 Civil - Application (19...).docx</div>
                                            <div className="file-size">100 KB</div>
                                        </div>
                                        <div className="progress-bar w-100 mt-2">
                                            <motion.div className="progress-fill" initial={{ width: '0%' }} animate={{ width: '86%' }} transition={{ duration: 2.2, ease: 'easeInOut' }} />
                                        </div>
                                    </div>
                                    <div className="file-close">&times;</div>

                                </div>

                            </div>
                        )}

                        {step === 3 && (
                            <div className="instant-cite-uploading">
                                <div className="file-row">
                                    <img src={fileIcon} alt="file" />
                                    <div className="file-meta">
                                        <div className="file-name">Filing 1 Civil - Application (19...).docx</div>
                                        <div className="file-size">100 KB</div>
                                    </div>
                                    <div className="file-close">&times;</div>
                                </div>
                            </div>
                        )}

                        {step !== 4 && (
                            <div className="cta-wrap">
                                <motion.div
                                    initial={false}
                                    animate={(step === 1 && topClicked) || (step === 3 && clicked) ? { y: 1, scale: 0.98 } : { y: 0, scale: 1 }}
                                    transition={{ duration: 0.18 }}
                                >
                                    <Button disabled={(step === 1 && topClicked) || (step === 3 && clicked)} className="button--primary-new button--full-width instant-cite-cta">Run Citation Check</Button>
                                </motion.div>
                                {step === 1 && (
                                    <motion.img
                                        src={mouseArrow}
                                        alt="cursor"
                                        className="cta-cursor"
                                        initial={{ x: 6, y: 6, opacity: 0 }}
                                        animate={topClicked ? { x: 6, y: 6, opacity: 1, scale: [1, 0.92, 1] } : { x: 6, y: 6, opacity: 1 }}
                                        transition={{ duration: topClicked ? 0.28 : 0.01 }}
                                    />
                                )}
                                {step === 3 && (
                                    <motion.img
                                        src={mouseArrow}
                                        alt="cursor"
                                        className="cta-cursor"
                                        initial={{ x: -40, y: 18, opacity: 0.95 }}
                                        animate={clicked ? { x: 6, y: 6, opacity: 1, scale: [1, 0.92, 1] } : { x: 6, y: 6, opacity: 1 }}
                                        transition={{ duration: clicked ? 0.28 : 0.01 }}
                                    />
                                )}
                            </div>
                        )}

                        {step === 4 && (
                            <div className="instant-cite-result">
                                <div className="result-title">Potential Problems</div>
                                <div className="result-case">Ferguson v. Tabah, 288 F.2d 665 (2d Cir. 1961)</div>
                                <div className="result-meter">
                                    <div className="meter-track" />
                                    <div className="meter-fill" />
                                    <div className="meter-note">33% relevant</div>
                                </div>
                                <div className="result-body">While this is a real case, the procedural posture on appeal by defendants in this case does not match your argument here.</div>
                                <div className="result-foot">1 citation flagged &middot; 13 verified correctly</div>
                            </div>
                        )}
                    </div>
                </div>
            </div>

            {!visualOnly && <div className="instant-cite-right">
                <SharedCardContent
                    icon={quoteIcon}
                    badgeTitle="Citation Intelligence & Hallucination Detection"
                    title="Rapid Cite Verification"
                    text="Drop in a draft from opposing counsel or a junior associate. Vienna validates citations and quoted language against sources, flags mismatches or missing support, and links you to the authority."
                    subtext="Catch bad cites and misquotes before filing."
                />
            </div>}
        </div>
    );
}

export default InstantCiteVerification;
